import { useCallback, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { TaxReturnFormValues } from '../types/tax-return';
import debounce from 'lodash/debounce';

export function useAutoSave(form: UseFormReturn<TaxReturnFormValues>, currentStep: number) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const isPaymentStep = currentStep === 4;

  const saveProgress = useCallback(async (data: TaxReturnFormValues) => {
    if (!data.id || isPaymentStep) return;

    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('tax_return_requests')
        .update({
          service_type: data.service_type,
          preferred_office: data.preferred_office,
          bsb: data.bsb,
          account_number: data.account_number,
          account_name: data.account_name,
          status: 'draft'
        })
        .eq('id', data.id);

      if (error) throw error;
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  }, [isPaymentStep]);

  // Debounced save function
  const debouncedSave = useCallback(
    debounce((data: TaxReturnFormValues) => saveProgress(data), 30000),
    [saveProgress]
  );

  // Watch form changes and trigger auto-save
  useEffect(() => {
    const subscription = form.watch((data) => {
      debouncedSave(data as TaxReturnFormValues);
    });

    return () => {
      subscription.unsubscribe();
      debouncedSave.cancel();
    };
  }, [form, debouncedSave]);

  return { isSaving, lastSaved };
}